/* eslint-disable jsx-control-statements/jsx-use-if-tag */
import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { signInWithPassword, requestOTP, signInWithOTP } from '../../services';

import { patientPageWidth } from '../../constant';

import { validPhone } from '../../utils/regex';
import { alertError } from '../../redux/stateActions';

import {
    GridContainer,
    GridItem,
    Title,
    Subtitle,
    ContainedButton,
    NewsCard,
    Header,
    Text,
    StyledCard,
    StyledCardContent,
    SignInForm,
} from '../../components';

import SelectHospital from '../../components/Hospital/SelectHospital';
import ReCAPTCHA from '../../components/ReCAPTCHA';

const Image1 = '/resources/images/image1.jpg';
const Image2 = '/resources/images/image2.jpg';

const STATE = Object.freeze({
    PASSWORD: 'PASSWORD',
    REQUEST: 'REQUEST',
    OTP: 'OTP',
    SELECT: 'SELECT',
});

const SignIn = ({ history }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const mobileS = useMediaQuery('(max-width:320px)');
    const mobileM = useMediaQuery('(max-width:375px)');

    const [loading, setLoading] = useState(false);
    const [state, setState] = useState(STATE.PASSWORD);
    const [inputValue, setInputValue] = useState({
        phone: '',
        password: '',
        otp: '',
    });
    const [OTPRef, setOTPRef] = useState('ABCB');
    // const [userId, setUserId] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [errors, setErrors] = useState([]);
    const [selectData, setSelectData] = useState({});

    const handleChange = (e) => {
        const newInputValue = { ...inputValue };
        const value = e.target.value;
        const name = e.target.name;
        newInputValue[name] = value;
        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleForgotPassword = () => {
        history.push('/forgot');
    };

    useEffect(() => {
        setErrors([]);
    }, [state]);

    const handleSuccessSignIn = (user, jwt, session) => {
        if (user.patients.length === 0) {
            return dispatch(alertError('Cannot find client data. Please check input value.'));
        } else {
            setState(STATE.SELECT);
            setSelectData({ role: 'Patient', id: user.id, jwt, session });
        }
    };

    const handleSignIn = async () => {
        const newErrors = [];

        if (!validPhone.test(inputValue.phone)) {
            newErrors.push({
                property: 'phone',
                message: 'Please provide phone number.',
            });
        }

        if (state === STATE.PASSWORD) {
            if (!inputValue.password) {
                newErrors.push({
                    property: 'password',
                    message: 'Please provide password.',
                });
            }
        } else {
            if (!inputValue.otp) {
                newErrors.push({
                    property: 'otp',
                    message: 'Please provide otp.',
                });
            }
        }

        if (newErrors.length) return setErrors(newErrors);

        setLoading(true);

        try {
            let res;
            if (state === STATE.PASSWORD) {
                res = await signInWithPassword({
                    mobileNumber: inputValue.phone,
                    password: inputValue.password,
                });
            } else {
                res = await signInWithOTP({
                    mobileNumber: inputValue.phone,
                    refCode: OTPRef,
                    otp: inputValue.otp,
                });
            }

            if (res.jwt) {
                handleSuccessSignIn(res.user, res.jwt, res.session);

                setLoading(false);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);

            if (_.isArray(error?.response?.data?.message)) {
                dispatch(
                    alertError(
                        `Cannot Signin: ${error?.response?.data?.message[0]?.messages[0]?.message}`
                    )
                );
            } else if (_.isString(error?.response?.data?.message)) {
                dispatch(alertError(`Cannot Signin: ${error?.response?.data?.message}`));
            } else {
                dispatch(alertError(`Cannot Signin: please try again later.`));
            }
        }
    };

    const handleSwitchToOTP = () => {
        if (state === STATE.PASSWORD) {
            if (countdown > 0) {
                setState(STATE.OTP);
            } else {
                setState(STATE.REQUEST);
            }
        } else {
            setState(STATE.PASSWORD);
        }
        setInputValue({ ...inputValue, password: '', otp: '' });
    };

    const handleRequestOTP = async () => {
        const newErrors = [];

        if (!validPhone.test(inputValue.phone)) {
            newErrors.push({
                property: 'phone',
                message: 'Please provide phone number.',
            });
        }

        if (newErrors.length) return setErrors(newErrors);

        setLoading(true);

        try {
            let res;
            if (state == STATE.REQUEST) {
                res = await requestOTP(inputValue.phone);
                if (res.refCode) {
                    setOTPRef(res.refCode);
                    // setUserId(res.users_id.id);
                    setState(STATE.OTP);
                    setTimer();
                } else {
                    throw new Error(res);
                }
            }
        } catch (error) {
            dispatch(
                alertError(`Cannot request OTP: ${JSON.stringify(error.response?.data?.message)}`)
            );
        }
        setLoading(false);
    };

    const handleResendOTP = async () => {
        setLoading(true);

        try {
            let res;
            res = await requestOTP(inputValue.phone);
            if (res.refCode) {
                setOTPRef(res.refCode);
                // setUserId(res.users_id.id);
                setState(STATE.OTP);
                setTimer();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(
                alertError(`Cannot request OTP: ${JSON.stringify(error.response?.data?.message)}`)
            );
            setTimer();
        }
        setLoading(false);
    };

    const setTimer = () => {
        let count = 30;
        setCountdown(count);
    };

    useEffect(() => {
        if (countdown > 0) {
            setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
        }
    }, [countdown]);

    // const handleRedirect = () => {
    //     history.push('/register');
    // };

    const onPressEnterPhoneInput = () => {
        if (state === STATE.REQUEST) {
            handleRequestOTP();
        }
    };

    const onPressEnterOTPInput = () => {
        if (state === STATE.OTP) {
            handleSignIn();
        }
    };

    const onPressEnterPasswordInput = () => {
        if (state === STATE.PASSWORD) {
            handleSignIn();
        }
    };

    /*---------- reCAPTCHA -----------*/
    const captchaRef = useRef(null);
    const [isVerify, setIsVerify] = useState(false);

    const handleVerifySuccess = () => {
        setIsVerify(true);
    };
    const handleVerifyError = () => {
        setIsVerify(false);
    };
    const handleVerifyExpire = () => {
        setIsVerify(false);
    };

    return (
        <div
            style={{
                padding: mobileM ? '0' : '0 24px',
                marginBottom: '20px',
                maxWidth: patientPageWidth,
            }}
        >
            <Header
                showGoBack1={!mdUp}
                childrenContainerStyle={{ textAlign: mdUp ? 'left' : 'center' }}
            />
            <GridContainer
                direction="row"
                alignItems="flex-start"
                style={{ paddingTop: mdUp ? '50px' : '20px', marginBottom: '20px' }}
            >
                {state !== STATE.SELECT && (
                    <GridItem sm>
                        <GridContainer margin={mdUp ? '0 0 10px 0' : undefined}>
                            <GridItem justifyContent={mdUp ? 'flex-start' : 'center'}>
                                <Text
                                    $bold
                                    $fontSize="24px"
                                    style={{ textAlign: mdUp ? 'left' : 'center' }}
                                >
                                    Welcome !
                                </Text>
                            </GridItem>
                            <GridItem
                                justifyContent={mdUp ? 'flex-start' : 'center'}
                                style={{ marginBottom: '10px' }}
                            >
                                <Text
                                    $fontSize="14px"
                                    $color="gray2"
                                    style={{ textAlign: mdUp ? 'left' : 'center' }}
                                >
                                    FRIDAY brings fine days.
                                </Text>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}
                {state === STATE.SELECT && (
                    <GridItem sm>
                        <GridContainer margin={mdUp ? '0 0 10px 0' : undefined}>
                            <GridItem justifyContent={mdUp ? 'flex-start' : 'center'}>
                                <Text
                                    $bold
                                    $fontSize="18px"
                                    style={{ textAlign: mdUp ? 'left' : 'center' }}
                                >
                                    Choose Counseling Service Account
                                </Text>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}
                <GridItem sm>
                    <GridContainer spacing={2}>
                        {state !== STATE.SELECT && (
                            <GridItem>
                                <StyledCard
                                    variant="outlined"
                                    padding={mobileS ? '24px 5px' : '24px'}
                                >
                                    <StyledCardContent
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            rowGap: '10px',
                                        }}
                                    >
                                        <SignInForm
                                            data={inputValue}
                                            onChange={handleChange}
                                            onForgotPassword={handleForgotPassword}
                                            onResendOTP={handleResendOTP}
                                            OTPRef={OTPRef}
                                            state={state}
                                            countdown={countdown}
                                            onPressEnterPhoneInput={onPressEnterPhoneInput}
                                            onPressEnterOTPInput={onPressEnterOTPInput}
                                            onPressEnterPasswordInput={onPressEnterPasswordInput}
                                            errors={errors}
                                        />
                                        <ReCAPTCHA
                                            ref={captchaRef}
                                            handleSuccess={handleVerifySuccess}
                                            handleError={handleVerifyError}
                                    handleExpire={handleVerifyExpire}
                                        />
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'flex-end',
                                                marginTop: '10px',
                                            }}
                                        >
                                            {state === STATE.PASSWORD || state === STATE.OTP ? (
                                                <ContainedButton
                                                    $noShadow
                                                    $width="215px"
                                                    onClick={handleSignIn}
                                                    loading={loading}
                                                    disabled={!isVerify}
                                                >
                                                    Sign In
                                                </ContainedButton>
                                            ) : (
                                                <ContainedButton
                                                    $noShadow
                                                    $width="215px"
                                                    onClick={handleRequestOTP}
                                                    loading={loading}
                                                    disabled={!isVerify}
                                                >
                                                    Request OTP
                                                </ContainedButton>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <Text
                                                $color="gray1"
                                                $fontSize="14px"
                                                style={{ textDecoration: 'underline' }}
                                                onClick={handleSwitchToOTP}
                                            >
                                                {state === STATE.PASSWORD
                                                    ? 'Sign in with OTP'
                                                    : 'Sign in with Password'}
                                            </Text>
                                        </div>
                                    </StyledCardContent>
                                </StyledCard>
                            </GridItem>
                        )}
                        {state === STATE.SELECT && (
                            <GridItem>
                                <SelectHospital data={selectData} />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>

            <NewsCard
                header="What do we do ?"
                src={Image1}
                $imgTitle="image1"
                $imgwidth="auto"
                $imgMaxWidth="200px"
                margin="15px 0 0 0"
            >
                <GridItem justifyContent="flex-start" margin="10px 0 0 0">
                    <Title>
                        FRIDAY เป็นโซลูชั่นสำหรับพัฒนาระบบการบริการให้คำปรึกษา และสุขภาพจิตทางไกล
                    </Title>
                </GridItem>
                <GridItem justifyContent="flex-start" margin="10px 0 0 0">
                    <Subtitle>
                        All-in-one platform ของเราเป็นสะพานเชื่อมระหว่างผู้ให้บริการและผู้รับบริการ
                        ด้านการให้คำปรึกษาและสุขภาพจิต ให้เข้าใกล้กันมากขึ้น
                        ลดค่าใช้จ่ายและเวลาที่ต้องเสียไปสำหรับการเดินทาง
                        ด้วยการบริการผ่านระบบวิดีโอคอลที่ทันสมัย
                        โดยที่คุณไม่ต้องลงทุนเพื่อเริ่มสร้างระบบใหม่และไม่ต้องเรียนรู้เรื่อง coding
                    </Subtitle>
                </GridItem>
            </NewsCard>

            {/* <NewsCard
                src={Image2}
                $imgTitle="image2"
                $imgwidth="auto"
                $imgMaxWidth="200px"
                margin="15px 0 0 0"
            >
                <GridItem justifyContent="flex-start" margin="10px 0 0 0">
                    <Title>
                        <Span $color="pink">Lorem ipsum</Span> dolor sit amet, conseter. Adipiscing
                        elit.
                    </Title>
                </GridItem>
                <GridItem justifyContent="flex-start" margin="10px 0 0 0">
                    <Subtitle>
                        Lorem ipsum dolor sit amet, conseter Adipiscing elit. Integer ut leo magna.
                        In et tortor ultricies magna molestie laoreet sit amet vel tortor.
                    </Subtitle>
                </GridItem>
            </NewsCard> */}
        </div>
    );
};

SignIn.propTypes = {
    history: PropTypes.object,
};

export default SignIn;
